import React, { useEffect, useRef, useState } from "react";

const N8N_WEBHOOK_URL = String(
   "https://n8n.srv1325067.hstgr.cloud/webhook/f85dc646-e069-41be-9d60-e350d665064d/chat",
).trim();

const CHAT_I18N = {
   en: {
      title: "IAAI",
      subtitle: "Chat privat conectat la n8n",
      footer: "",
      getStarted: "Discutie noua",
      inputPlaceholder: "Scrie mesajul tau...",
      closeButtonTooltip: "Inchide",
   },
};

const URL_PATTERN = /https?:\/\/[^\s<>"']+/gi;
const TRAILING_URL_PUNCTUATION = /[),.;!?]+$/;

function toURL(value) {
   try {
      return new URL(String(value || "").trim());
   } catch (error) {
      return null;
   }
}

function isYouTubeHost(hostname) {
   const host = String(hostname || "").toLowerCase();
   return (
      host === "youtube.com" ||
      host === "www.youtube.com" ||
      host.endsWith(".youtube.com") ||
      host === "youtu.be" ||
      host.endsWith(".youtu.be")
   );
}

function isYouTubeUrl(urlValue) {
   const parsed = toURL(urlValue);
   return Boolean(parsed && isYouTubeHost(parsed.hostname));
}

function extractYouTubeVideoId(urlValue) {
   const parsed = toURL(urlValue);
   if (!parsed || !isYouTubeHost(parsed.hostname)) return "";

   const host = parsed.hostname.toLowerCase();

   if (host.includes("youtu.be")) {
      return parsed.pathname.split("/").filter(Boolean)[0] || "";
   }

   if (parsed.pathname === "/watch") {
      return parsed.searchParams.get("v") || "";
   }

   if (parsed.pathname.startsWith("/shorts/")) {
      return parsed.pathname.split("/")[2] || "";
   }

   if (parsed.pathname.startsWith("/embed/")) {
      return parsed.pathname.split("/")[2] || "";
   }

   return "";
}

function extractYouTubeSearchQuery(urlValue) {
   const parsed = toURL(urlValue);
   if (!parsed || !isYouTubeHost(parsed.hostname)) return "";
   if (parsed.pathname !== "/results") return "";

   return (
      parsed.searchParams.get("search_query") ||
      parsed.searchParams.get("q") ||
      ""
   );
}

function normalizeSearchQuery(query) {
   const safeQuery = String(query || "").replace(/\+/g, " ");

   try {
      return decodeURIComponent(safeQuery).replace(/\s+/g, " ").trim();
   } catch (error) {
      return safeQuery.replace(/\s+/g, " ").trim();
   }
}

function titleFromAnchor(anchor, fallbackIndex = 0) {
   if (!(anchor instanceof HTMLAnchorElement)) {
      return `Videoclip ${fallbackIndex + 1}`;
   }

   const anchorText = String(anchor.textContent || "").trim();
   if (anchorText && !/^https?:\/\//i.test(anchorText)) return anchorText;

   const previousText = String(anchor.previousSibling?.textContent || "");
   const candidate = previousText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .pop();

   if (candidate && !/^https?:\/\//i.test(candidate)) {
      return candidate.replace(/[:-]\s*$/, "");
   }

   const searchQuery = normalizeSearchQuery(extractYouTubeSearchQuery(anchor.href));
   if (searchQuery) return searchQuery;

   return `Videoclip ${fallbackIndex + 1}`;
}

function replaceTextNodeUrls(textNode) {
   if (!textNode || !textNode.parentNode) return false;

   const sourceText = String(textNode.nodeValue || "");
   if (!sourceText.includes("http://") && !sourceText.includes("https://")) {
      return false;
   }

   const regex = new RegExp(URL_PATTERN);
   const fragment = document.createDocumentFragment();
   let lastIndex = 0;
   let changed = false;
   let match = regex.exec(sourceText);

   while (match) {
      const rawUrl = match[0];
      const cleanUrl = rawUrl.replace(TRAILING_URL_PUNCTUATION, "");
      const trailing = rawUrl.slice(cleanUrl.length);
      const parsed = toURL(cleanUrl);

      fragment.append(sourceText.slice(lastIndex, match.index));

      if (parsed) {
         const link = document.createElement("a");
         link.href = parsed.toString();
         link.target = "_blank";
         link.rel = "noopener noreferrer nofollow";
         link.className = "chat-inline-link";
         link.textContent = cleanUrl;
         fragment.append(link);
         changed = true;
      } else {
         fragment.append(rawUrl);
      }

      if (trailing) fragment.append(trailing);
      lastIndex = match.index + rawUrl.length;
      match = regex.exec(sourceText);
   }

   if (!changed) return false;

   fragment.append(sourceText.slice(lastIndex));
   textNode.parentNode.replaceChild(fragment, textNode);
   return true;
}

function linkifyMarkdownNode(rootElement) {
   if (!rootElement) return false;

   const textNodes = [];
   const walker = document.createTreeWalker(
      rootElement,
      NodeFilter.SHOW_TEXT,
      {
         acceptNode(node) {
            if (!node?.nodeValue) return NodeFilter.FILTER_REJECT;
            if (
               !node.nodeValue.includes("http://") &&
               !node.nodeValue.includes("https://")
            ) {
               return NodeFilter.FILTER_REJECT;
            }

            const parentElement = node.parentElement;
            if (!parentElement) return NodeFilter.FILTER_REJECT;

            if (parentElement.closest("a, code, pre, .chat-video-links")) {
               return NodeFilter.FILTER_REJECT;
            }

            return NodeFilter.FILTER_ACCEPT;
         },
      },
      false,
   );

   while (walker.nextNode()) textNodes.push(walker.currentNode);

   return textNodes.some((node) => replaceTextNodeUrls(node));
}

function getUniqueYouTubeAnchors(rootElement) {
   if (!rootElement) return [];

   const result = [];
   const seen = new Set();

   const anchors = rootElement.querySelectorAll("a[href]");
   anchors.forEach((anchor) => {
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.closest(".chat-video-links")) return;
      if (!isYouTubeUrl(anchor.href)) return;

      const key = anchor.href;
      if (seen.has(key)) return;
      seen.add(key);
      result.push(anchor);
   });

   return result;
}

function createYouTubeCard(anchor, index) {
   const card = document.createElement("a");
   card.className = "chat-video-card";
   card.href = anchor.href;
   card.target = "_blank";
   card.rel = "noopener noreferrer nofollow";

   const media = document.createElement("div");
   media.className = "chat-video-card__media";

   const videoId = extractYouTubeVideoId(anchor.href);
   const searchQuery = normalizeSearchQuery(extractYouTubeSearchQuery(anchor.href));

   if (videoId) {
      const image = document.createElement("img");
      image.loading = "lazy";
      image.decoding = "async";
      image.alt = titleFromAnchor(anchor, index);
      image.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
      media.append(image);
   } else {
      media.classList.add("chat-video-card__media--search");

      const searchLabel = document.createElement("span");
      searchLabel.className = "chat-video-card__query";
      searchLabel.textContent = searchQuery
         ? ``
         : "Deschide rezultatele pe YouTube";
      media.append(searchLabel);
   }

   const meta = document.createElement("div");
   meta.className = "chat-video-card__meta";

   const title = document.createElement("p");
   title.className = "chat-video-card__title";
   title.textContent = titleFromAnchor(anchor, index);

   const hint = document.createElement("span");
   hint.className = "chat-video-card__hint";
   hint.textContent = "Deschide pe YouTube";

   meta.append(title, hint);
   card.append(media, meta);

   return card;
}

function enhanceBotMessages(targetElement) {
   if (!targetElement) return;

   const markdownNodes = targetElement.querySelectorAll(
      ".chat-message-from-bot .chat-message-markdown",
   );

   markdownNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      linkifyMarkdownNode(node);

      const anchors = getUniqueYouTubeAnchors(node);
      const signature = anchors.map((anchor) => anchor.href).join("|");
      const previousSignature = node.dataset.videoLinksSignature || "";
      const existingBlock = node.querySelector(
         ".chat-video-links[data-generated='true']",
      );

      if (!signature) {
         if (existingBlock) existingBlock.remove();
         if (previousSignature) delete node.dataset.videoLinksSignature;
         return;
      }

      if (signature === previousSignature && existingBlock) return;

      if (existingBlock) existingBlock.remove();

      const linksBlock = document.createElement("section");
      linksBlock.className = "chat-video-links";
      linksBlock.dataset.generated = "true";

      const heading = document.createElement("p");
      heading.className = "chat-video-links__title";
      heading.textContent =
         anchors.length > 1 ? "Videoclipuri recomandate" : "Videoclip recomandat";

      const list = document.createElement("div");
      list.className = "chat-video-links__list";
      anchors.forEach((anchor, index) => {
         list.append(createYouTubeCard(anchor, index));
      });

      linksBlock.append(heading, list);
      node.append(linksBlock);
      node.dataset.videoLinksSignature = signature;
   });
}

function createSessionId() {
   const cryptoObj = window.crypto;

   if (cryptoObj?.randomUUID) {
      return `admin-chat-${cryptoObj.randomUUID()}`;
   }

   if (cryptoObj?.getRandomValues) {
      const bytes = new Uint8Array(16);
      cryptoObj.getRandomValues(bytes);
      const hex = Array.from(bytes, (b) =>
         b.toString(16).padStart(2, "0"),
      ).join("");
      return `admin-chat-${hex}`;
   }

   return `admin-chat-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function getPersistentSessionId() {
   const key = "n8n-admin-chat-session-id";
   let existing = localStorage.getItem(key);

   if (!existing) {
      existing = createSessionId();
      localStorage.setItem(key, existing);
   }

   return existing;
}

function ChatbotWorkspace() {
   const targetRef = useRef(null);
   const chatAppRef = useRef(null);
   const observerRef = useRef(null);
   const rafRef = useRef(0);
   const [loadError, setLoadError] = useState("");

   useEffect(() => {
      const targetElement = targetRef.current;
      if (!targetElement || !N8N_WEBHOOK_URL) return undefined;

      let cancelled = false;
      setLoadError("");

      (async () => {
         try {
            const [n8nModule] = await Promise.all([
               import("@n8n/chat"),
               import("@n8n/chat/style.css"),
            ]);

            if (cancelled || !targetElement) return;

            const sessionId = getPersistentSessionId();

            targetElement.innerHTML = "";
            chatAppRef.current = n8nModule.createChat({
               webhookUrl: N8N_WEBHOOK_URL,
               target: targetElement,
               mode: "fullscreen",
               webhookConfig: { method: "POST" },
               sessionId,
               loadPreviousSession: false,
               showWelcomeScreen: false,
               enableStreaming: true,
               metadata: {
                  panel: "admin-chatbot",
               },
               i18n: CHAT_I18N,
               initialMessages: [
                  "Salut! Acesta este chat-ul conectat la n8n.",
                  "Poti incepe direct cu o intrebare.",
               ],
            });

            const scheduleEnhance = () => {
               if (rafRef.current) cancelAnimationFrame(rafRef.current);
               rafRef.current = requestAnimationFrame(() => {
                  rafRef.current = 0;
                  enhanceBotMessages(targetElement);
               });
            };

            scheduleEnhance();

            observerRef.current = new MutationObserver(() => {
               scheduleEnhance();
            });

            observerRef.current.observe(targetElement, {
               subtree: true,
               childList: true,
               characterData: true,
            });
         } catch (error) {
            if (!cancelled) {
               setLoadError(
                  "Nu am putut initializa @n8n/chat. Verifica Chat Trigger URL, CORS, SSL/proxy si configurarea Load Previous Session din n8n.",
               );
            }
         }
      })();

      return () => {
         cancelled = true;
         observerRef.current?.disconnect();
         observerRef.current = null;
         if (rafRef.current) cancelAnimationFrame(rafRef.current);
         rafRef.current = 0;
         try {
            chatAppRef.current?.unmount?.();
         } catch (error) {
         }
         chatAppRef.current = null;
         targetElement.innerHTML = "";
      };
   }, []);

   if (!N8N_WEBHOOK_URL) {
      return (
         <section className="n8n-chatbot-setup">
            <h2>Lipseste webhook-ul n8n</h2>
            <p>
               Adauga <code>REACT_APP_N8N_CHAT_WEBHOOK_URL</code> in{" "}
               <code>.env</code>, apoi reporneste aplicatia.
            </p>
            <pre>REACT_APP_N8N_CHAT_WEBHOOK_URL=https://n8n.../webhook/...</pre>
         </section>
      );
   }

   if (loadError) {
      return (
         <section className="n8n-chatbot-setup">
            <h2>Eroare initializare chat</h2>
            <p>{loadError}</p>
         </section>
      );
   }

   return (
      <section className="n8n-chatbot-shell">
         <div
            ref={targetRef}
            id="n8n-chat"
            className="n8n-chatbot-shell__target"
         ></div>
      </section>
   );
}

export default ChatbotWorkspace;
